import React, { useEffect, useRef, useState } from "react";
import { AlertTriangle, ShieldAlert, X } from "lucide-react";

// ─── Confirmation centralisée avant toute action qui modifie réellement les données ───
// Usage : const ok = await confirmAction({ title, message, confirmLabel, variant, requireText })
let _open = null;

export function confirmAction(opts) {
  if (!_open) return Promise.resolve(window.confirm(opts.message || opts.title || "Confirmer ?"));
  return _open(opts);
}

const VARIANTS = {
  danger: { icon: ShieldAlert, iconCls: "text-red-600 bg-red-50", btnCls: "bg-red-600 hover:bg-red-500" },
  warning: { icon: AlertTriangle, iconCls: "text-amber-600 bg-amber-50", btnCls: "bg-amber-600 hover:bg-amber-500" },
  default: { icon: AlertTriangle, iconCls: "text-violet-600 bg-violet-50", btnCls: "bg-violet-600 hover:bg-violet-500" },
};

export function ConfirmProvider({ children }) {
  const [state, setState] = useState(null); // { opts, resolve }
  const [typed, setTyped] = useState("");
  const cancelRef = useRef(null);

  useEffect(() => {
    _open = (opts) => new Promise((resolve) => {
      setTyped("");
      setState({ opts, resolve });
    });
    return () => { _open = null; };
  }, []);

  useEffect(() => {
    if (state) cancelRef.current?.focus();
  }, [state]);

  const close = (result) => {
    state?.resolve(result);
    setState(null);
  };

  useEffect(() => {
    if (!state) return;
    const onKey = (e) => { if (e.key === "Escape") close(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  const opts = state?.opts || {};
  const v = VARIANTS[opts.variant] || VARIANTS.default;
  const Icon = v.icon;
  const needsText = !!opts.requireText;
  const textOk = !needsText || typed.trim().toUpperCase() === String(opts.requireText).toUpperCase();

  return (
    <>
      {children}
      {state && (
        <div
          className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center p-3 sm:p-6"
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="confirm-dialog-title"
          data-testid="confirm-dialog"
        >
          <div className="absolute inset-0 bg-slate-950/50 backdrop-blur-[2px]" onClick={() => close(false)} />
          <div className="relative w-full max-w-md rounded-2xl bg-white shadow-2xl border border-slate-200 p-5" style={{ animation: "jnMaterialize 0.22s cubic-bezier(0.22,1,0.36,1) both" }}>
            <button onClick={() => close(false)} aria-label="Fermer" className="absolute top-3 right-3 h-7 w-7 rounded-full hover:bg-slate-100 flex items-center justify-center text-slate-400">
              <X className="h-4 w-4" />
            </button>
            <div className="flex items-start gap-3">
              <div className={`h-10 w-10 rounded-full flex items-center justify-center shrink-0 ${v.iconCls}`}>
                <Icon className="h-5 w-5" />
              </div>
              <div className="min-w-0 flex-1">
                <div id="confirm-dialog-title" className="text-[15px] font-bold text-slate-900">{opts.title || "Confirmer cette action ?"}</div>
                {opts.message && (
                  <p className="mt-1.5 text-[13px] text-slate-600 leading-relaxed whitespace-pre-line" data-testid="confirm-dialog-message">{opts.message}</p>
                )}
                {needsText && (
                  <div className="mt-3">
                    <label className="text-[11px] font-semibold text-slate-500">
                      Saisissez <span className="font-mono font-bold text-slate-800">{opts.requireText}</span> pour confirmer :
                    </label>
                    <input
                      value={typed}
                      onChange={(e) => setTyped(e.target.value)}
                      className="mt-1 w-full h-9 rounded-lg border border-slate-300 px-3 text-[13px] font-mono focus:outline-none focus:ring-2 focus:ring-red-400"
                      placeholder={opts.requireText}
                      autoFocus
                      data-testid="confirm-dialog-input"
                    />
                  </div>
                )}
              </div>
            </div>
            <div className="mt-4 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
              {!opts.hideCancel && (
                <button
                  ref={cancelRef}
                  onClick={() => close(false)}
                  className="h-9 px-4 rounded-lg border border-slate-300 text-[13px] font-semibold text-slate-700 hover:bg-slate-50 transition-colors"
                  data-testid="confirm-dialog-cancel"
                >
                  {opts.cancelLabel || "Annuler"}
                </button>
              )}
              {opts.secondaryLabel && (
                <button
                  onClick={() => close("secondary")}
                  className="h-9 px-4 rounded-lg border border-slate-300 text-[13px] font-semibold text-slate-700 hover:bg-slate-50 transition-colors"
                  data-testid="confirm-dialog-secondary"
                >
                  {opts.secondaryLabel}
                </button>
              )}
              <button
                onClick={() => close(true)}
                disabled={!textOk || opts.confirmDisabled}
                className={`h-9 px-4 rounded-lg text-[13px] font-bold text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${v.btnCls}`}
                data-testid="confirm-dialog-confirm"
              >
                {opts.confirmLabel || "Confirmer"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
